import type { Metadata, MetadataRoute } from "next"
import { getPublicBaseUrl } from "@/shared/config/seo-env"
import { parseSeoDate } from "@/shared/lib/seo-date"

import { DEFAULT_LOCALE, SUPPORTED_LOCALES, normalizeLocale, type SupportedLocale } from "@/shared/lib/locale"

export const BASE_URL = getPublicBaseUrl()
// 로케일 원본은 leaf 모듈(`@/shared/lib/locale`)에 있다 — 클라 번들이 seo 전체를 끌지 않게.
// 기존 import 경로를 유지하기 위해 그대로 재수출한다.
export { DEFAULT_LOCALE, SUPPORTED_LOCALES, normalizeLocale }
export type { SupportedLocale }

/**
 * 검색엔진 색인 대상 locale. en/cn/jp는 본문(리뷰·병원 소개)이 미번역 ko 중복이라
 * 색인 가치가 없어 제외한다. 현재 proxy는 이 locale들을 ko로 임시 302하고,
 * hreflang·sitemap에서도 제외한다. 번역이 준비되면 proxy의 임시 리다이렉트를 제거하고
 * 이 배열에 locale을 추가해 다시 공개한다.
 */
export const INDEXABLE_LOCALES: readonly SupportedLocale[] = [DEFAULT_LOCALE]

export const LOCALE_LANGUAGE_MAP: Record<SupportedLocale, string> = {
  ko: "ko-KR",
  en: "en-US",
  cn: "zh-CN",
  jp: "ja-JP",
}

export const OG_BRAND_ICON_PATH = "/icons/revieworks-icon.png"
export const DEFAULT_OG_IMAGE = OG_BRAND_ICON_PATH
export const OG_IMAGE_SIZE = { width: 128, height: 128 }

export const HOME_METADATA: Record<SupportedLocale, { title: string; description: string }> = {
  ko: {
    title: "리뷰드 | 실제 환자 리뷰로 찾는 믿을 수 있는 병원 정보",
    description:
      "전국 병원, 치과, 의료기관 정보 검색과 실제 이용자 리뷰를 한 번에 확인하세요. 위치, 진료과목, 평점 비교 가능.",
  },
  en: {
    title: "Reviewed | Trusted hospital information from real patient reviews",
    description:
      "Search hospitals and clinics nationwide and read real patient reviews in one place.",
  },
  cn: {
    title: "Reviewed | 通过真实患者评价寻找可信赖的医院信息",
    description: "一站式搜索全国医院、牙科和医疗机构信息，并查看真实用户评价。",
  },
  jp: {
    title: "Reviewed | 実際の患者レビューで探す信頼できる病院情報",
    description: "全国の病院・歯科・医療機関情報と実際の利用者レビューを一度に確認できます。",
  },
}

export type MetadataProps = {
  params?: Promise<{ locale: string }>
}

export const absoluteUrl = (path = "", baseUrl = BASE_URL) => {
  if (/^https?:\/\//.test(path)) return path
  const normalizedPath = path === "" || path === "/" ? "" : path.startsWith("/") ? path : `/${path}`
  return `${baseUrl}${normalizedPath}`
}

export const localizedUrl = (
  path: string,
  locale: SupportedLocale = DEFAULT_LOCALE,
  baseUrl = BASE_URL,
) => {
  const normalizedPath = path === "" || path === "/" ? "" : path.startsWith("/") ? path : `/${path}`
  return `${baseUrl}/${locale}${normalizedPath}`
}

export const buildLanguageAlternates = (
  path: string,
  baseUrl = BASE_URL,
  locales: readonly SupportedLocale[] = INDEXABLE_LOCALES,
) => {
  const normalizedPath = path === "" || path === "/" ? "" : path.startsWith("/") ? path : `/${path}`
  const defaultLocale = locales.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : locales[0]
  const languages: Record<string, string> = {
    "x-default": `${baseUrl}/${defaultLocale}${normalizedPath}`,
  }

  for (const locale of locales) {
    languages[LOCALE_LANGUAGE_MAP[locale]] = `${baseUrl}/${locale}${normalizedPath}`
  }

  return languages
}

const toSitemapLastModified = (value?: string) => {
  return parseSeoDate(value)
}

/**
 * 병원 페이지 sitemap 엔트리. API가 보고한 locale이라도 INDEXABLE_LOCALES에 없으면
 * 제외한다 — 현재 ko로 임시 리다이렉트되는 locale URL을 sitemap에 실으면 안 되기 때문.
 * 전 locale을 무조건 광고하면 URL 수가 locale 배수로 부풀어 크롤 예산을 소진시키고,
 * 미번역(사실상 중복) 페이지가 색인 품질 신호를 깎는다.
 */
export const buildHospitalSitemapEntry = (
  hospitalOriginId: number,
  locales: readonly SupportedLocale[] = [DEFAULT_LOCALE],
  updatedAt?: string,
): MetadataRoute.Sitemap[number] => {
  const path = `hospital/${hospitalOriginId}`
  const indexable = locales.filter((locale) => INDEXABLE_LOCALES.includes(locale))
  const availableLocales: readonly SupportedLocale[] =
    indexable.length > 0 ? indexable : [DEFAULT_LOCALE]
  const primaryLocale = availableLocales.includes(DEFAULT_LOCALE)
    ? DEFAULT_LOCALE
    : availableLocales[0]

  return {
    url: localizedUrl(path, primaryLocale, BASE_URL),
    lastModified: toSitemapLastModified(updatedAt),
    // 단일 locale뿐이면 alternates 자체가 무의미하므로 생략한다.
    ...(availableLocales.length > 1
      ? { alternates: { languages: buildLanguageAlternates(path, BASE_URL, availableLocales) } }
      : {}),
  }
}

export const buildLocalizedPageMetadata = ({
  locale = DEFAULT_LOCALE,
  path = "",
  canonicalUrl: providedCanonicalUrl,
  title,
  description,
  imagePath = DEFAULT_OG_IMAGE,
  type = "website",
  includeCanonical = true,
  robots,
}: {
  locale?: string
  path?: string
  /**
   * 페이지가 이미 계산한 정본 URL. 페이지별 canonical·Open Graph·JSON-LD가
   * 같은 URL을 사용해야 하는 경우 이 값을 넘긴다.
   */
  canonicalUrl?: string
  title: string
  description: string
  imagePath?: string
  type?: "website" | "article"
  includeCanonical?: boolean
  /**
   * 크롤러별 색인 지시어. 생략하면 robots 메타를 내보내지 않는다(= 기본 색인 허용).
   * 구글에만 색인을 끄려면 `GOOGLE_ONLY_NOINDEX_ROBOTS` 를 넘긴다.
   */
  robots?: Metadata["robots"]
}): Metadata => {
  const normalizedLocale = normalizeLocale(locale)
  const canonicalUrl = providedCanonicalUrl ?? localizedUrl(path, normalizedLocale, BASE_URL)
  const imageUrl = absoluteUrl(imagePath, BASE_URL)

  return {
    title,
    description,
    ...(robots ? { robots } : {}),
    ...(includeCanonical
      ? {
        alternates: {
          canonical: canonicalUrl,
          // 색인 locale이 ko 하나뿐인 동안 hreflang은 내보내지 않는다.
          // (현재 ko로 임시 리다이렉트되는 en/cn/jp를 "번역판"으로 가리키는 모순 방지.
          //  INDEXABLE_LOCALES에 locale이 추가되면 자동으로 다시 열린다)
          ...(INDEXABLE_LOCALES.length > 1
            ? { languages: buildLanguageAlternates(path, BASE_URL) }
            : {}),
        },
      }
      : {}),
    openGraph: {
      title,
      description,
      url: canonicalUrl,
      type,
      siteName: "리뷰드",
      locale: LOCALE_LANGUAGE_MAP[normalizedLocale].replace("-", "_"),
      images: [{ url: imageUrl, ...OG_IMAGE_SIZE, alt: "리뷰드" }],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [imageUrl],
    },
  }
}

export const SITEMAP_PATHS = [
  "/sitemap.xml",
  "/sitemap/hospital01/sitemap.xml",
  "/sitemap/hospital02/sitemap.xml",
  "/sitemap/hospital03/sitemap.xml",
  "/sitemap/hospital04/sitemap.xml",
  "/sitemap/hospital05/sitemap.xml",
  "/sitemap/hospital06/sitemap.xml",
  "/sitemap/hospital07/sitemap.xml",
  "/sitemap/hospital08/sitemap.xml",
  "/sitemap/hospital09/sitemap.xml",
  "/sitemap/hospital-priority/sitemap.xml",
] as const

export const buildSitemapUrls = (baseUrl = BASE_URL) =>
  SITEMAP_PATHS.map((path) => `${baseUrl}${path}`)

export const SEO_CONTENT_LAST_REVIEWED = "2026-08-04"

export const SEO_STATIC_LAST_MODIFIED = {
  home: new Date(`${SEO_CONTENT_LAST_REVIEWED}T00:00:00.000Z`),
} as const

const schemaId = (baseUrl: string, id: string) => `${baseUrl}/#${id}`

export const serializeJsonLd = (value: unknown) =>
  JSON.stringify(value).replace(/</g, "\\u003c")

export const buildReviewedOrganizationSchema = (baseUrl = BASE_URL) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": schemaId(baseUrl, "organization"),
  name: "리뷰드",
  legalName: "(주)리걸케어",
  alternateName: ["Reviewed", "reviewed.co.kr"],
  url: baseUrl,
  logo: {
    "@type": "ImageObject",
    url: `${baseUrl}${OG_BRAND_ICON_PATH}`,
    // schema.org ImageObject 에는 `alt` 가 없다 — 넣으면 전 페이지가 구조화 데이터 검증 오류로 잡힌다.
    caption: "리뷰드",
  },
  sameAs: ["https://www.legalcare.ai"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "대덕대로 582, 4층 402호",
    addressLocality: "유성구",
    addressRegion: "대전광역시",
    addressCountry: "KR",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "010-4432-9325",
    email: "cs@legalcare.ai",
    contactType: "customer support",
    areaServed: "KR",
    availableLanguage: ["ko-KR"],
  },
})

export const buildReviewedWebsiteSchema = (baseUrl = BASE_URL) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": schemaId(baseUrl, "website"),
  name: "리뷰드",
  alternateName: ["Reviewed", "reviewed.co.kr"],
  url: baseUrl,
  inLanguage: "ko-KR",
  publisher: {
    "@id": schemaId(baseUrl, "organization"),
  },
  potentialAction: {
    "@type": "SearchAction",
    target: `${localizedUrl("/search/result", DEFAULT_LOCALE, baseUrl)}?query={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
})

export const buildHomeWebPageSchema = (
  locale: string = DEFAULT_LOCALE,
  baseUrl = BASE_URL,
) => {
  const normalizedLocale = normalizeLocale(locale)
  const url = localizedUrl("", normalizedLocale, baseUrl)
  const metadata = HOME_METADATA[normalizedLocale]

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: metadata.title,
    description: metadata.description,
    isPartOf: {
      "@id": schemaId(baseUrl, "website"),
    },
    publisher: {
      "@id": schemaId(baseUrl, "organization"),
    },
    inLanguage: LOCALE_LANGUAGE_MAP[normalizedLocale],
    dateModified: SEO_STATIC_LAST_MODIFIED.home.toISOString(),
    about: ["병원 리뷰", "의료기관 검색", "치료 사례", "진료과목", "병원 위치"],
  }
}
